import { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import { Transition } from "components"
import { lang } from "config"
import { useAuth } from "contexts/auth-context"
import SelectSearchable from "components/forms/select-searchable"
import Input from "components/forms/input"
import ErrorMessage from "components/forms/error-message"
import useBranchRepositories from "repositories/branch"
import { getBranchList, getCurrentEntity } from "helpers/general-helper"
import { getUser } from "helpers/auth-helper"
import { defaultUomForEntity, loadMasterUomFactors, masterUomFactor, normalizeUomCode } from "helpers/uom-helper"
import ProductPickerModal from "pages/purchase/product-picker-modal"

// Retur toko offline ke HO Atalla: pilih outlet ASAL (toko offline, info) + outlet
// TUJUAN (stok Atalla bertambah). Input per-item UOM (PCS/LUSIN/PASANG) seperti PO;
// qty DISIMPAN dalam PCS (qty × faktor) — uom hanya penanda dokumen. Post = stok
// MASUK ke outlet tujuan (backend OfflineReturnService).
const UOM_OPTIONS = ["PCS", "LUSIN", "PASANG"]

function todayISO() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

function num(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

// qty (dalam uom) -> total PCS bulat (mirror backend: round(qty × faktor)).
function toPcs(qty, uom) {
  return Math.round(num(qty) * masterUomFactor(uom))
}

// 250726 (permintaan user): ambil harga PERSIS seperti form PO. dozen_store_price =
// harga per LUSIN (12 pcs); satuan lusin (LUSIN/LSN/faktor 12) pakai itu dulu, selain
// itu harga per-pcs × faktor. Harga DITAMPILKAN per-satuan (mengikuti UOM).
function isDozenUom(u) {
  const uu = String(u || "").trim().toUpperCase()
  return uu === "LUSIN" || uu === "LSN" || masterUomFactor(u) === 12
}

// Harga dasar per-PCS dari produk ps_list (kandidat sama dgn form PO).
function pickCostPrice(p) {
  const candidates = [
    p?.price, p?.pcs_price, p?.maximum_retail_price,
    p?.purchase_price, p?.buy_price, p?.cost_price, p?.hpp, p?.cogs,
  ]
  for (const c of candidates) {
    const n = Number(c)
    if (Number.isFinite(n) && n > 0) return n
  }
  return 0
}

// Harga per-satuan terpilih (mengikuti UOM): lusin → dozen_store_price bila ada,
// selain itu harga pcs × faktor.
function costForUom(p, uom) {
  const dozen = num(p?.dozen_store_price)
  return isDozenUom(uom) && dozen > 0 ? dozen : pickCostPrice(p) * masterUomFactor(uom)
}

async function apiFetch(path, { method = "GET", body, signal } = {}) {
  const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
  const url = `${base}${path}`
  // Sisipkan user_id sesi UI supaya backend mengisi created_by/updated_by (tanpa ini "SYSTEM").
  const userId = getUser()?.id
  const payload = method !== "GET" && userId ? { user_id: userId, ...(body || {}) } : body
  const res = await fetch(url, {
    method,
    signal,
    headers: { "Content-Type": "application/json" },
    body: payload ? JSON.stringify(payload) : null,
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok || json?.success === 0) {
    const flat = json?.errors && typeof json.errors === "object"
      ? Object.values(json.errors).flat().filter(Boolean)
      : []
    const msg = flat.length ? String(flat[0]) : (json?.message || "Request gagal")
    const err = json?.error ? `: ${json.error}` : ""
    throw new Error(msg + err)
  }
  return json
}

let _rowSeq = 0
const nextRowKey = () => `row_${++_rowSeq}`

export default function OfflineReturnForm() {
  const { id } = useParams()
  const isEditRoute = Boolean(id)

  const { can, currentBranch, user } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  // Daftar outlet SE-ENTITAS user (mirror retur pembelian).
  const { data: allOutletData } = useBranchRepositories({ paginate: false, unrestricted: true })
  const branchSelection = useMemo(() => {
    const rows = Array.isArray(allOutletData) ? allOutletData : allOutletData?.data ?? []
    const entity = getCurrentEntity()
    const filtered = entity ? rows.filter((b) => !b?.entity || b.entity === entity) : rows
    return filtered.length ? filtered : (getBranchList() || [])
  }, [allOutletData])

  // Header
  const [sourceOutletId, setSourceOutletId] = useState(null) // toko offline asal
  const [destOutletId, setDestOutletId] = useState(currentBranch?.id ?? null) // stok Atalla tujuan
  const [returnDate, setReturnDate] = useState(todayISO())
  const [reason, setReason] = useState("")
  const [status, setStatus] = useState("DRAFT")
  const [returnId, setReturnId] = useState(id || null)
  const [returnNumber, setReturnNumber] = useState("")
  // 250726 (permintaan user): kondisi barang — GOOD (bagus→nomor RA) / BAD (jelek→RJ).
  // Menentukan format nomor retur; dikunci setelah dibuat (nomor sudah terbit).
  const [goodsCondition, setGoodsCondition] = useState("GOOD")

  // 250726 (permintaan user): satuan GLOBAL (semua item) — PCS/LUSIN/PASANG. Qty per
  // baris diketik dalam satuan ini; disimpan PCS (qty × faktor). Mengikuti pola retur
  // pembelian (satu satuan utk seluruh dokumen).
  // 290726: default entitas diisi SINKRON saat mount (lihat ho-order/form).
  const [globalUom, setGlobalUom] = useState(() => defaultUomForEntity(getCurrentEntity(), null))

  // 290726 (permintaan user): satuan default ikut ENTITAS user (VISION→PASANG,
  // ATALLA→LUSIN). HANYA untuk retur BARU — saat edit, satuan dipulihkan dari
  // item tersimpan (lihat hydrate di bawah) supaya nilai lama tidak tertimpa.
  useEffect(() => {
    if (isEditRoute) return
    let cancelled = false
    loadMasterUomFactors().then((names) => {
      if (cancelled) return
      setGlobalUom((prev) => normalizeUomCode(prev) || prev)
    })
    return () => { cancelled = true }
  }, [isEditRoute])

  // Items: [{ key, product_unit_id, product_label, product_unit_name, uom, qty, unit_cost, notes }]
  const [items, setItems] = useState([])
  const [errors, setErrors] = useState({})
  const [acting, setActing] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [initialLoading, setInitialLoading] = useState(isEditRoute)

  const isDraft = String(status || "").toUpperCase() === "DRAFT"
  const isLocked = !isDraft

  useEffect(() => { loadMasterUomFactors() }, [])

  const outletName = (oid) => {
    const id0 = oid?.id ?? oid
    if (id0 == null) return ""
    const found = (branchSelection || []).find((b) => String(b?.id) === String(id0))
    return found?.name || ""
  }

  // ── Load existing (edit route) ──────────────────────────────────────────────
  useEffect(() => {
    if (!isEditRoute) { setInitialLoading(false); return }
    const run = async () => {
      try {
        setInitialLoading(true)
        nProgress.start()
        const res = await apiFetch(`/offline-returns/${id}`)
        const payload = res?.data || res
        const ret = payload?.return || payload

        setReturnId(ret?.id ?? id)
        setReturnNumber(ret?.return_number || "")
        setStatus(ret?.status ?? "DRAFT")
        if (ret?.source_outlet_id != null) setSourceOutletId(ret.source_outlet_id)
        if (ret?.dest_outlet_id != null) setDestOutletId(ret.dest_outlet_id)
        if (ret?.goods_condition) setGoodsCondition(String(ret.goods_condition).toUpperCase() === "BAD" ? "BAD" : "GOOD")
        if (ret?.return_date) setReturnDate(String(ret.return_date).slice(0, 10))
        if (ret?.reason != null) setReason(ret.reason || "")

        const rawItems = Array.isArray(payload?.items) ? payload.items : (ret?.items ?? [])
        {
          // Pulihkan satuan global dari item pertama (fallback PCS).
          // 290726: label lama dipetakan ke kode master (LUSIN→LSN, PASANG→PSG).
          const firstUom = normalizeUomCode(rawItems?.[0]?.uom || "PCS")
          if (firstUom) setGlobalUom(firstUom)
        }
        setItems(
          (Array.isArray(rawItems) ? rawItems : []).map((x) => {
            const uom = String(x.uom || "PCS").toUpperCase()
            const pcs = num(x.qty_returned)
            const factor = masterUomFactor(uom)
            const qtyInUom = factor > 1 ? Math.round((pcs / factor) * 100) / 100 : pcs
            return {
              key: nextRowKey(),
              product_unit_id: String(x.product_unit_id),
              product_label:
                x.product_unit?.long_name ||
                x.product_unit?.name ||
                x.product_unit_name ||
                String(x.product_unit_id),
              product_unit_name:
                x.product_unit_name ||
                x.product_unit?.long_name ||
                x.product_unit?.name ||
                String(x.product_unit_id),
              uom: UOM_OPTIONS.includes(uom) ? uom : "PCS",
              qty: qtyInUom,
              // Tersimpan per-PCS → tampilkan per-satuan (× faktor), mengikuti UOM.
              unit_cost:
                x.unit_cost === null || x.unit_cost === undefined
                  ? ""
                  : Math.round(num(x.unit_cost) * (factor || 1)),
              dozen_store_price: 0, // tak tersimpan; recompute pakai basis per-pcs
              notes: x.notes ?? "",
            }
          })
        )
      } catch (e) {
        toast.error(e?.message || "Gagal memuat retur")
      } finally {
        setInitialLoading(false)
        nProgress.done()
      }
    }
    run()
  }, [isEditRoute, id])

  // ── Item helpers ────────────────────────────────────────────────────────────
  const addPickedItems = (entries) => {
    if (!entries?.length) return
    setItems((prev) => {
      let next = [...prev]
      for (const { product: p, qty } of entries) {
        const pid = String(p?.product_unit_id ?? "")
        if (!pid) continue
        const label = p?.product_unit_long_name || p?.product_unit || p?.sku || p?.product_model || pid
        const idx = next.findIndex((x) => String(x.product_unit_id) === pid)
        if (idx >= 0) {
          next[idx] = { ...next[idx], qty: num(next[idx].qty) + (num(qty) > 0 ? num(qty) : 1) }
        } else {
          // Harga otomatis mengikuti satuan (sumber ambil harga = pola PO).
          const dozen = num(p?.dozen_store_price)
          next = [
            {
              key: nextRowKey(),
              product_unit_id: pid,
              product_label: label,
              product_unit_name: label,
              uom: globalUom,
              qty: num(qty) > 0 ? num(qty) : 1,
              unit_cost: Math.round(costForUom(p, globalUom)), // per-satuan (tampilan)
              base_pcs_price: num(pickCostPrice(p)), // acuan konversi antar satuan
              dozen_store_price: dozen, // simpan mentah utk recompute saat ganti satuan
              notes: "",
            },
            ...next,
          ]
        }
      }
      return next
    })
    setPickerOpen(false)
  }

  const updateRow = (key, patch) =>
    setItems((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  const removeRow = (key) => setItems((prev) => prev.filter((r) => r.key !== key))

  const totalPcs = useMemo(
    () => items.reduce((acc, r) => acc + toPcs(r.qty, globalUom), 0),
    [items, globalUom]
  )

  // Total nilai retur = Σ (qty × harga per-satuan). Harga per-satuan sudah mengikuti UOM.
  const grandTotal = useMemo(
    () => items.reduce((acc, r) => acc + num(r.qty) * num(r.unit_cost), 0),
    [items]
  )

  // ── Payload / validation ────────────────────────────────────────────────────
  const buildItems = () =>
    items
      .filter((r) => toPcs(r.qty, globalUom) > 0)
      .map((r) => ({
        product_unit_id: r.product_unit_id,
        product_unit_name: r.product_unit_name || r.product_label || null,
        qty_returned: toPcs(r.qty, globalUom), // PCS
        uom: globalUom,
        // Harga DISIMPAN per-PCS (unit_cost ÷ faktor) — konsisten dgn qty PCS & pola PO.
        // Cetak faktur mengalikan lagi × faktor supaya tampil per-satuan.
        unit_cost:
          r.unit_cost === "" || r.unit_cost == null
            ? null
            : num(r.unit_cost) / (masterUomFactor(globalUom) || 1),
        notes: r.notes || null,
      }))

  const validate = (payloadItems) => {
    const e = {}
    if (!sourceOutletId) e.source_outlet_id = "Outlet asal " + lang.field_required
    if (!destOutletId) e.dest_outlet_id = "Outlet tujuan " + lang.field_required
    if (!payloadItems.length) e.items = "Tambahkan minimal 1 produk yang diretur"
    setErrors(e)
    return Object.keys(e).length === 0
  }

  // ── Actions ─────────────────────────────────────────────────────────────────
  const createReturn = async () => {
    const payloadItems = buildItems()
    if (!validate(payloadItems)) return
    try {
      setActing(true)
      nProgress.start()
      toast.loading("Membuat retur...", { id: "or-create" })
      const body = {
        source_outlet_id: sourceOutletId?.id ?? sourceOutletId,
        dest_outlet_id: destOutletId?.id ?? destOutletId,
        goods_condition: goodsCondition,
        return_date: returnDate,
        reason: reason || null,
        items: payloadItems,
      }
      const res = await apiFetch(`/offline-returns`, { method: "POST", body })
      toast.success(res?.message || "Retur berhasil dibuat", { id: "or-create" })
      navigate("/offline-return", {
        replace: true,
        state: { back: "Daftar Retur Toko Offline", from: location.pathname, transition: "slide" },
      })
    } catch (e) {
      toast.error(e?.message || "Gagal membuat retur", { id: "or-create" })
    } finally {
      nProgress.done()
      setActing(false)
    }
  }

  const saveDraft = async () => {
    if (isLocked || !returnId) return
    const payloadItems = buildItems()
    if (!validate(payloadItems)) return
    try {
      setActing(true)
      nProgress.start()
      toast.loading("Menyimpan draft...", { id: "or-save" })
      await apiFetch(`/offline-returns/${returnId}/items`, {
        method: "POST",
        body: {
          items: payloadItems,
          // 250726 (permintaan user): outlet ASAL (info) masih bisa diganti saat DRAFT.
          source_outlet_id: sourceOutletId?.id ?? sourceOutletId,
          reason: reason || null,
        },
      })
      toast.success("Draft tersimpan", { id: "or-save" })
      navigate("/offline-return", { state: location.state, replace: true })
    } catch (e) {
      toast.error(e?.message || "Gagal menyimpan draft", { id: "or-save" })
    } finally {
      nProgress.done()
      setActing(false)
    }
  }

  const postReturn = async () => {
    if (!returnId || isLocked) return
    if (!can?.("offline-return.post")) return
    if (!window.confirm(`Posting retur ${returnNumber || returnId}? Stok akan MASUK ke outlet tujuan.`)) return
    try {
      setActing(true)
      nProgress.start()
      toast.loading("Memposting...", { id: "or-post" })
      const res = await apiFetch(`/offline-returns/${returnId}/post`, { method: "POST" })
      const data = res?.data || res
      toast.success(res?.message || "Retur berhasil diposting", { id: "or-post" })
      setStatus(data?.status || "POSTED")
    } catch (e) {
      toast.error(e?.message || "Gagal memposting", { id: "or-post" })
    } finally {
      nProgress.done()
      setActing(false)
    }
  }

  const cancelReturn = async () => {
    if (!returnId) return
    if (!can?.("offline-return.cancel")) return
    if (String(status).toUpperCase() !== "DRAFT") return
    const r = window.prompt("Alasan pembatalan (opsional):", "")
    if (r === null) return
    try {
      setActing(true)
      nProgress.start()
      toast.loading("Membatalkan...", { id: "or-cancel" })
      const res = await apiFetch(`/offline-returns/${returnId}/cancel`, {
        method: "POST",
        body: r ? { reason: r } : null,
      })
      const data = res?.data || res
      toast.success(res?.message || "Retur dibatalkan", { id: "or-cancel" })
      setStatus(data?.status || "CANCELLED")
    } catch (e) {
      toast.error(e?.message || "Gagal membatalkan", { id: "or-cancel" })
    } finally {
      nProgress.done()
      setActing(false)
    }
  }

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-sm text-neutral-500">Memuat retur...</div>
      </div>
    )
  }

  const destOid = destOutletId?.id ?? destOutletId
  const canSubmitNew = sourceOutletId && destOutletId && items.some((r) => toPcs(r.qty, globalUom) > 0)

  return (
    <Transition type="fade">
      <div className="pb-8 space-y-6">
        {/* Header actions */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-lg font-semibold text-neutral-900">
              {returnId ? "Edit Retur Toko Offline" : "Buat Retur Toko Offline"}
            </h1>
            <p className="text-xs text-neutral-500">
              Retur toko offline (belum live) ke HO Atalla. Posting akan MENAMBAH stok di outlet tujuan
              (stok toko asal tidak berubah). Qty per baris disimpan dalam PCS.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/offline-return", { state: location.state })}
              className="px-4 py-3 text-xs font-semibold text-neutral-700 border border-neutral-200 rounded-xl hover:bg-neutral-50"
            >
              Back
            </button>

            {returnId && (
              <button
                type="button"
                onClick={() => {
                  const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
                  window.open(`${base}/offline-returns/${returnId}/print`, "_blank")
                }}
                className="px-4 py-3 text-xs font-semibold text-white bg-sky-700 rounded-xl hover:bg-sky-600"
              >
                Print Faktur
              </button>
            )}

            {can?.("offline-return.cancel") && returnId && isDraft && (
              <button
                type="button"
                disabled={acting}
                onClick={cancelReturn}
                className="px-4 py-3 text-xs font-semibold text-white bg-red-600 rounded-xl hover:bg-red-500 disabled:opacity-60"
              >
                {acting ? "..." : "Batal"}
              </button>
            )}

            {can?.("offline-return.post") && returnId && isDraft && (
              <button
                type="button"
                disabled={acting}
                onClick={postReturn}
                className="px-4 py-3 text-xs font-semibold text-white bg-green-700 rounded-xl hover:bg-green-600 disabled:opacity-60"
              >
                {acting ? "..." : "Posting"}
              </button>
            )}

            {(can?.("offline-return.create") || can?.("offline-return.update")) && returnId && (
              <button
                type="button"
                disabled={acting || isLocked}
                onClick={saveDraft}
                className={`px-4 py-3 text-xs font-semibold text-white rounded-xl ${
                  isLocked ? "bg-neutral-400 cursor-not-allowed" : "bg-brand-900 hover:bg-brand-800"
                } disabled:opacity-60`}
              >
                {acting ? "..." : "Simpan Draft"}
              </button>
            )}

            {can?.("offline-return.create") && !returnId && (
              <button
                type="button"
                disabled={acting || !canSubmitNew}
                onClick={createReturn}
                className={`px-4 py-3 text-xs font-semibold text-white rounded-xl ${
                  canSubmitNew ? "bg-brand-900 hover:bg-brand-800" : "bg-neutral-400 cursor-not-allowed"
                } disabled:opacity-60`}
              >
                {acting ? "..." : "Buat Retur"}
              </button>
            )}
          </div>
        </div>

        {/* Header form */}
        <div className="border border-neutral-200 rounded-xl">
          <div className="grid grid-cols-2 gap-4 px-4 py-5 sm:p-6">
            <div>
              <label className="block text-xs text-neutral-700">
                Toko Offline (Asal)<sup className="text-xs text-red-700"> *</sup>
              </label>
              <SelectSearchable
                // 250726 (permintaan user): outlet ASAL (info) tetap bisa diganti selama DRAFT.
                disabled={isLocked}
                filter={(v) => v.name}
                onChange={(v) => setSourceOutletId(v?.id ?? v ?? null)}
                selection={branchSelection}
                isLoading={false}
                value={sourceOutletId}
                keyValue={(v) => v.id}
                title={(v) => v.name}
                placeholder="-- Pilih Toko Offline --"
              />
              <ErrorMessage error={errors?.source_outlet_id} />
            </div>

            <div>
              <label className="block text-xs text-neutral-700">
                HO Atalla (Tujuan Stok)<sup className="text-xs text-red-700"> *</sup>
              </label>
              <SelectSearchable
                disabled={Boolean(returnId)}
                filter={(v) => v.name}
                onChange={(v) => {
                  setDestOutletId(v?.id ?? v ?? null)
                  if (!isEditRoute) setItems([]) // harga/katalog terikat outlet tujuan
                }}
                selection={branchSelection}
                isLoading={false}
                value={destOutletId}
                keyValue={(v) => v.id}
                title={(v) => v.name}
                placeholder="-- Pilih Outlet Atalla --"
              />
              <ErrorMessage error={errors?.dest_outlet_id} />
            </div>

            <div>
              <label className="block text-xs text-neutral-700">
                Kondisi Barang<sup className="text-xs text-red-700"> *</sup>
              </label>
              <select
                disabled={Boolean(returnId)}
                value={goodsCondition}
                onChange={(e) => setGoodsCondition(e.target.value)}
                className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
              >
                <option value="GOOD">Barang Bagus (RA)</option>
                <option value="BAD">Barang Jelek (RJ)</option>
              </select>
              <p className="mt-1 text-[11px] text-neutral-400">
                Menentukan format nomor: bagus → RA, jelek → RJ.
              </p>
            </div>

            <div>
              <label className="block text-xs text-neutral-700">Tanggal Retur</label>
              <input
                type="date"
                disabled={Boolean(returnId)}
                value={returnDate}
                onChange={(e) => setReturnDate(e.target.value)}
                className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
              />
            </div>

            <div>
              <label className="block text-xs text-neutral-700">{lang.status}</label>
              <Input disabled value={status || "DRAFT"} />
            </div>

            {returnNumber ? (
              <div>
                <label className="block text-xs text-neutral-700">No. Retur</label>
                <Input disabled value={returnNumber} />
              </div>
            ) : null}

            <div className="col-span-2">
              <label className="block text-xs text-neutral-700">{lang.notes}</label>
              <textarea
                disabled={isLocked && Boolean(returnId)}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
                placeholder="Alasan retur (opsional)"
              />
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="border border-neutral-200 rounded-xl">
          <div className="px-4 py-5 space-y-3 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-neutral-900">Produk Diretur</div>
                <div className="text-xs text-neutral-500">
                  Pilih satuan (berlaku semua item), lalu isi qty per baris.
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-xs text-neutral-600">
                  Baris: <b>{items.length}</b> | Total: <b>{totalPcs}</b> pcs
                </div>
                <button
                  type="button"
                  disabled={isLocked || !destOid}
                  onClick={() => setPickerOpen(true)}
                  className={`px-4 py-3 text-xs font-semibold text-white rounded-xl ${
                    !isLocked && destOid ? "bg-brand-900 hover:bg-brand-800" : "bg-neutral-400 cursor-not-allowed"
                  }`}
                >
                  + Pilih Produk
                </button>
              </div>
            </div>

            <ErrorMessage error={errors?.items} />

            {/* 250726 (permintaan user): satuan GLOBAL untuk seluruh item. */}
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-neutral-600">Satuan (semua item)</label>
              <select
                value={globalUom}
                disabled={isLocked}
                onChange={(e) => {
                  const next = e.target.value
                  const prevUom = globalUom
                  setGlobalUom(next)
                  // Ganti satuan = ubah TAMPILAN saja, jumlah barang riil (total pcs) TETAP.
                  // - qty: konversi angka (pcs ÷ faktor baru) supaya total pcs tak berubah.
                  // - harga: pertahankan per-pcs (÷faktor lama ×faktor baru); lusin snap dozen.
                  const prevF = masterUomFactor(prevUom) || 1
                  const nextF = masterUomFactor(next) || 1
                  setItems((prev) =>
                    prev.map((r) => {
                      const pcs = num(r.qty) * prevF
                      const nextQty = Math.round((pcs / nextF) * 100) / 100 // 2 desimal (konvensi)
                      // 290726: harga dikonversi dari harga DASAR per PCS, bukan dari
                      // harga satuan sebelumnya (LUSIN -> PCS dulu = dozen/12, salah).
                      const basePcs = num(r.base_pcs_price) > 0
                        ? num(r.base_pcs_price)
                        : num(r.unit_cost) / prevF
                      let cost = basePcs * nextF
                      if (isDozenUom(next) && num(r.dozen_store_price) > 0) cost = num(r.dozen_store_price)
                      return {
                        ...r,
                        uom: next,
                        qty: nextQty,
                        unit_cost: Math.round(cost),
                        base_pcs_price: basePcs,
                      }
                    })
                  )
                }}
                className="w-32 px-3 py-2 text-xs border rounded-lg border-neutral-200 focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-200"
              >
                {UOM_OPTIONS.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>

            <div className="overflow-x-auto border border-neutral-200 rounded-xl">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Produk</th>
                    <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Satuan</th>
                    <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Qty</th>
                    <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Total (pcs)</th>
                    <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Harga /{globalUom}</th>
                    <th className="px-3 py-3 text-xs font-medium text-right uppercase text-neutral-500">Subtotal</th>
                    <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Catatan</th>
                    <th className="px-3 py-3" />
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-3 py-6 text-xs text-center text-neutral-500">
                        {destOid ? "Belum ada produk. Klik “Pilih Produk”." : "Pilih outlet tujuan dulu."}
                      </td>
                    </tr>
                  )}
                  {items.map((r) => (
                    <tr key={r.key} className="transition-colors hover:bg-brand-50">
                      <td className="px-3 py-3 text-xs font-medium text-neutral-900">{r.product_label}</td>
                      <td className="px-3 py-3 text-xs font-medium text-neutral-700">{globalUom}</td>
                      <td className="px-3 py-3 text-xs">
                        <input
                          type="number"
                          min={0}
                          step="any"
                          disabled={isLocked}
                          value={r.qty}
                          onChange={(e) => updateRow(r.key, { qty: e.target.value })}
                          className="w-24 px-2 py-2 text-xs border rounded-lg border-neutral-200 focus:outline-none focus:ring disabled:bg-neutral-200"
                        />
                      </td>
                      <td className="px-3 py-3 text-xs font-medium text-neutral-700">{toPcs(r.qty, globalUom)}</td>
                      <td className="px-3 py-3 text-xs">
                        <input
                          type="number"
                          min={0}
                          step={1}
                          disabled={isLocked}
                          value={r.unit_cost}
                          onChange={(e) => updateRow(r.key, { unit_cost: e.target.value })}
                          placeholder="0"
                          className="w-28 px-2 py-2 text-xs border rounded-lg border-neutral-200 focus:outline-none focus:ring disabled:bg-neutral-200"
                        />
                      </td>
                      <td className="px-3 py-3 text-xs font-medium text-right text-neutral-800">
                        {num(r.qty) * num(r.unit_cost) > 0
                          ? `Rp ${(num(r.qty) * num(r.unit_cost)).toLocaleString("id-ID")}`
                          : "-"}
                      </td>
                      <td className="px-3 py-3 text-xs">
                        <input
                          disabled={isLocked}
                          value={r.notes}
                          onChange={(e) => updateRow(r.key, { notes: e.target.value })}
                          className="w-48 px-2 py-2 text-xs border rounded-lg border-neutral-200 focus:outline-none focus:ring disabled:bg-neutral-200"
                        />
                      </td>
                      <td className="px-3 py-3 text-xs text-right">
                        {!isLocked && (
                          <button
                            type="button"
                            onClick={() => removeRow(r.key)}
                            className="px-2 py-1 text-[11px] font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                          >
                            Hapus
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {items.length > 0 && (
                  <tfoot className="bg-neutral-50">
                    <tr>
                      <td colSpan={5} className="px-3 py-3 text-xs font-semibold text-right uppercase text-neutral-600">
                        Total Nilai Retur
                      </td>
                      <td className="px-3 py-3 text-xs font-bold text-right text-neutral-900">
                        Rp {grandTotal.toLocaleString("id-ID")}
                      </td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      </div>

      <ProductPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onAdd={addPickedItems}
        outletId={destOid}
        uom={globalUom}
      />
    </Transition>
  )
}
